import styled from 'styled-components';

import {
  colorBlack900,
  colorGray,
  colorBlueGray,
  colorPrimary,
  colorGrayLight,
  colorWhite,
  colorBlueLight,
  colorAccent,
} from 'styles';

import Input from 'components/commons/Input';
import Icon from 'components/commons/Icon';

export const AutoCompleteInput = styled(Input)`
  color: ${colorBlack900};
  cursor: pointer;
  display: block;
  font-size: 14px;
  font-weight: 400;
  outline: none;
  padding: 12px 16px;
  height: 46px;
  width: 100%;

  &::placeholder {
    font-size: 14px;
    font-weight: 400;
    line-height: 1;
  }
`;

export const InnerAutoCompleteWrapper = styled.div`
  padding-left: 17px;
  display: flex;
  align-items: center;
`;

export const InnerAutoCompleteInput = styled(AutoCompleteInput)`
  padding-left: 10px;
  border: 0;
`;

export const ToggleButtonLabel = styled.span`
  overflow: hidden;
  padding-right: 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

export const ToggleButtonIcon = styled(Icon)`
  transition: fill 0.3s;
  fill: ${colorGray};
  top: calc(50% - 8px / 2);
  right: 16px;

  ${({ mobile }) => {
    if (mobile) {
      return `
      position: absolute;
    `;
    }

    return '';
  }}
`;

const toggleShared = `
  position: relative;
  appearance: none;
  align-items: center;
  background-color: ${colorWhite};
  border-radius: 2px;
  border: 1px solid ${colorBlueGray};
  color: ${colorBlack900};
  cursor: pointer;
  display: flex;
  outline: none;
  text-align: left;
  transition: color 0.3s;
  padding: 15px;
  width: 100%;
`;

export const ToggleButton = styled.button`
  ${toggleShared}

  ${({ active }) => {
    if (active) {
      return `
        color: ${colorPrimary} !important;

        & ${ToggleButtonIcon} {
          fill: ${colorPrimary} !important;
          transform: rotate(180deg);
        }
      `;
    }

    return '';
  }}

  font-size: ${({ size }) => {
    if (size === 'large') {
      return `18px`;
    }

    if (size === 'small') {
      return `12px`;
    }

    return `14px`;
  }};

  font-weight: ${({ size }) => {
    if (size === 'large') {
      return `600`;
    }

    return 400;
  }};

  line-height: ${({ size }) => {
    if (size === 'large') {
      return `24px`;
    }

    return 1;
  }};

  /* stylelint-disable */
  &:hover,
  &:active,
  &.focus-visible {
    color: ${colorPrimary} !important;
    border-color: ${colorBlueGray};

    ${ToggleButtonIcon} {
      fill: ${colorPrimary};
    }
  }

  ${AutoCompleteInput} + & {
    border-width: 0;
    bottom: 1px;
    color: rgba(0, 0, 0, 0);
    height: 44px;
    position: absolute;
    right: 1px;
    justify-content: center;
    width: 50px;
  }

  ${AutoCompleteInput} + & ${ToggleButtonLabel} {
    display: none;
  }
  /* stylelint-enable */
`;

export const MobileSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const MobileSelect = styled.select`
  ${toggleShared}
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  padding: 15px 32px 15px 15px;

  font-size: ${({ size }) => {
    if (size === 'large') {
      return `18px`;
    }

    if (size === 'small') {
      return `12px`;
    }

    return `16px`;
  }};

  font-weight: ${({ size }) => {
    if (size === 'large') {
      return `600`;
    }

    return 400;
  }};

  line-height: ${({ size }) => {
    if (size === 'large') {
      return `24px`;
    }

    if (size === 'small') {
      return 1;
    }

    return '19px';
  }};

  &:focus {
    color: ${colorPrimary} !important;
    border-color: ${colorBlueGray};
    font-size: 16px;
  }

  &:focus + ${/* sc-sel */ ToggleButtonIcon} {
    fill: ${colorPrimary} !important;
    transform: rotate(180deg);
  }

  ${({ active }) => {
    if (active) {
      return `
      color: ${colorPrimary};
    `;
    }

    return '';
  }}

  option {
    font-size: 16px;
  }
`;

export const OptionsList = styled.div`
  background-color: ${colorWhite};
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.14);
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  left: 0;
  top: 100%;
  position: absolute;
  width: 100%;
  z-index: 5;
  max-height: 330px;
  overflow-y: auto;
`;

export const OptionsLabel = styled.span`
  background-color: ${colorGrayLight};
  color: ${colorGray};
  display: block;
  font-size: 12px;
  font-weight: bold;
  padding: 10px 16px;
  text-transform: uppercase;
  width: 100%;
`;

export const CleanBtn = styled.a`
  background-color: rgba(0, 0, 0, 0);
  border: 0;
  position: relative;
  outline: none;
  margin: 0 8px;
  z-index: 1;
  height: 14px;
  padding: 6px;

  ::after {
    content: '';
    height: 14px;
    border-left: 2px solid ${colorGray};
    position: absolute;
    transform: rotate(45deg);
    right: 5px;
    top: 0;
  }

  ::before {
    content: '';
    height: 14px;
    border-left: 2px solid ${colorGray};
    position: absolute;
    transform: rotate(-45deg);
    right: 5px;
    top: -0;
  }
`;

export const Divider = styled.div`
  width: 100%;
  height: 0;
  overflow: hidden;
  border-top-width: ${({ width }) => (width ? `${width}px` : '1px')};
  border-top-style: solid;
  border-top-color: ${({ color }) => {
    switch (color) {
      case 'green':
        return colorPrimary;
      default:
        return colorBlueLight;
    }
  }};
`;

export const HiddenLinks = styled.span`
  font-size: 14px;
  white-space: nowrap;
  color: ${colorGray};
`;

export const ToggleButtonActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

export const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  position: relative;

  ${props => {
    if (props['data-type'] === 'link') {
      return `
        display: inline-flex;

        ${MobileSelectWrapper} {
          width: unset;
        }

        ${ToggleButton},
        ${MobileSelect} {
          position: relative;
          background-color: transparent;
          color: ${colorAccent};
          border: 0;

          &:after {
            content: '';
            position: absolute;
            top: -8px;
            bottom: -8px;
            left: 0;
            right: 0;
            z-index: 1;
          }
        }

        ${ToggleButton} {
          font-size: 12px;
          line-height: 16px;
          padding: 0;
        }

        ${MobileSelect} {
          padding: 0 16px 0 0;
        }

        ${ToggleButtonIcon} {
          fill: ${colorAccent};
          right: 0px;
        }
  
        ${ToggleButtonActions} {
          margin-left: unset;
        }

        ${OptionsList} {
          top: calc(100% + 13px);
          width: 288px;
        }
      `;
    }

    return `
      display: flex;
      width: 100%;
      
      ${ToggleButton} {
        width: 100%;
        background-color: ${colorWhite};
        border-radius: 2px;
        border: 1px solid ${colorBlueGray};
        padding: 15px;
      }
  `;
  }};
`;
